<template>
  <div class="mt-12 lg:grid lg:grid-cols-[260px_1fr] lg:gap-12">
    <div
      role="tablist"
      aria-label="Companies"
      class="flex overflow-x-auto overflow-y-hidden border-b border-gunMetal lg:block lg:overflow-visible lg:border-b-0 lg:border-l"
    >
      <button
        v-for="(job, index) in jobs"
        :key="job.company"
        role="tab"
        type="button"
        :aria-selected="selectedJob === index"
        class="-mb-px flex shrink-0 items-center gap-3 whitespace-nowrap border-b-2 px-5 py-3 font-source-code text-sm transition lg:-ml-px lg:w-full lg:border-b-0 lg:border-l-2"
        :class="
          selectedJob === index
            ? 'border-turquoise bg-turquoise/5 text-turquoise'
            : 'border-transparent text-white/60 hover:bg-turquoise/5 hover:text-turquoise'
        "
        @click="selectedJob = index"
      >
        <img
          :src="job.logo"
          :alt="job.company + ' logo'"
          class="h-6 w-6 rounded-full object-cover"
        />
        {{ job.company }}
      </button>
    </div>

    <Transition name="fade" mode="out-in">
      <div
        :key="selectedJob"
        role="tabpanel"
        class="min-h-[22rem] pt-8 lg:pt-1"
      >
        <div
          v-for="(role, index) in jobs[selectedJob].roles"
          :key="role.title"
          :class="index > 0 ? 'mt-10' : ''"
        >
          <h3 class="font-outfit text-2xl font-bold text-paleBlue">
            {{ role.title }}
            <span v-if="index === 0" class="text-turquoise"
              >@ {{ jobs[selectedJob].company }}</span
            >
          </h3>

          <p class="mt-1 font-source-code text-sm text-white/60">
            {{ role.period }}
          </p>

          <p class="mt-4 font-outfit text-lg text-white/80">
            {{ role.description }}
          </p>
        </div>

        <ul class="mt-8 flex flex-wrap gap-2">
          <li
            v-for="skill in jobs[selectedJob].skills"
            :key="skill"
            class="rounded-full bg-turquoise/10 px-3 py-1 font-source-code text-xs text-turquoise"
          >
            {{ skill }}
          </li>
        </ul>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { ref } from "vue";

import abraLogo from "../assets/abra_logo.jpeg";
import fridayFinanceLogo from "../assets/logo_friday_finance.png";
import zapietLogo from "../assets/zapiet_logo.png";
import devsquadLogo from "../assets/devsquad_logo.jpeg";
import lbaLogo from "../assets/lba_logo.jpeg";

const selectedJob = ref(0);

const jobs = ref([
  {
    company: "Abra Promotions",
    logo: abraLogo,
    roles: [
      {
        title: "Staff Software Engineer",
        period: "Jun 2025 - Now",
        description:
          "I provide technical leadership across Abra's product development while staying hands on. I define architectural standards, lead solution design for complex initiatives, mentor engineers and help with hiring through technical interviews.",
      },
      {
        title: "Senior Software Engineer",
        period: "Feb 2024 - Jun 2025",
        description:
          "I designed, developed and maintained Abra's core product features across the full development lifecycle. I led feature delivery from technical design to deployment and built commerce experiences on Shopify Plus with Node.js, GraphQL and Next.js.",
      },
    ],
    skills: [
      "Node.js",
      "TypeScript",
      "Next.js",
      "React",
      "GraphQL",
      "PostgreSQL",
      "Prisma",
      "Google Cloud",
      "Shopify Plus",
    ],
  },
  {
    company: "Friday Finance",
    logo: fridayFinanceLogo,
    roles: [
      {
        title: "Senior Full Stack Engineer",
        period: "Sep 2022 - Feb 2024",
        description:
          "I developed and maintained Friday Finance's main product, building custom Vue and Nuxt components and integrating third party APIs. I also improved database design, security and query performance, and wrote code guidelines and Storybook documentation that made onboarding and bug hunting much faster.",
      },
    ],
    skills: [
      "Node.js",
      "TypeScript",
      "Nuxt",
      "Vue",
      "GraphQL",
      "PostgreSQL",
      "Prisma",
      "TailwindCSS",
      "Storybook",
    ],
  },
  {
    company: "Zapiet",
    logo: zapietLogo,
    roles: [
      {
        title: "Senior Full Stack Developer",
        period: "Aug 2020 - Sep 2022",
        description:
          "I implemented automated tests with PHPUnit, PestPHP and Laravel Dusk and wired them into Bitbucket CI/CD pipelines with Docker, which cut errors reaching end users by 60%. I built frontend features with React and Shopify App Bridge, maintained Laravel and MySQL backends and became the go to developer for adopting new Shopify features.",
      },
    ],
    skills: [
      "Laravel",
      "React",
      "Node.JS",
      "MySQL",
      "Redis",
      "Docker",
      "CI/CD",
      "Shopify Plus",
      "Git",
    ],
  },
  {
    company: "DevSquad",
    logo: devsquadLogo,
    roles: [
      {
        title: "Full Stack Engineer",
        period: "Aug 2018 - Aug 2020",
        description:
          "I built and tested features with Laravel, Livewire and React, improving software quality with automated unit, integration and end to end tests. Query optimization and database tuning improved API response times by 40% and my bug fixes reduced customer reported issues by 20%.",
      },
    ],
    skills: [
      "Laravel",
      "Livewire",
      "React",
      "PHPUnit",
      "MySQL",
      "Redis",
      "TailwindCSS",
      "Multitenancy",
      "Jira",
    ],
  },
  {
    company: "LBA",
    logo: lbaLogo,
    roles: [
      {
        title: "Full Stack Engineer (Freelance)",
        period: "Mar 2018 - Aug 2018",
        description:
          "As a freelance engineer at LBA Diligências Jurídicas I managed server infrastructure on Digital Ocean and cut costs by 20% while keeping the platform reliable. I built the frontend with Livewire, AlpineJS and TailwindCSS, improved load times by 20% and introduced automated testing with PHPUnit and Laravel Dusk.",
      },
    ],
    skills: [
      "Laravel",
      "Livewire",
      "AlpineJS",
      "TailwindCSS",
      "MySQL",
      "Redis",
      "PHPUnit",
      "Digital Ocean",
      "Linux",
    ],
  },
]);
</script>
